import { useEffect, useState } from "react";
import { GraduationCap, Building2, ShieldCheck, Lock } from "lucide-react";
import AdminLayout from "../../layouts/AdminLayout";
import { useToast } from "../../components/ToastContext";
import Select from "../../components/common/Select";
import {
  createAdminUser,
  createPlatformAdminUser,
  getOrganizations,
} from "../../api/services/platformAdminService";

const TABS = [
  {
    key: "platform_admin",
    label: "Platform Admin",
    icon: ShieldCheck,
    showOrganization: false,
    organizationLabel: "",
    organizationPlaceholder: "",
  },
  {
    key: "company_admin",
    label: "Company Admin",
    icon: Building2,
    showOrganization: true,
    organizationLabel: "Company Name",
    organizationPlaceholder: "Enter company name",
  },
  {
    key: "university_admin",
    label: "University Admin",
    icon: GraduationCap,
    showOrganization: true,
    organizationLabel: "University Name",
    organizationPlaceholder: "Enter university name",
    locked: true,
  },
];

const emptyForm = {
  organization: "",
  firstName: "",
  lastName: "",
  email: "",
  username: "",
};

const inputClass =
  "w-full rounded-lg border border-(--color-border) bg-(--color-bg)/50 px-4 py-2.5 text-sm text-(--color-text-primary) placeholder:text-(--color-text-muted) focus:outline-none focus:border-(--color-primary) focus:shadow-[0_0_0_3px_var(--color-primary-100)] transition-all";

export default function RegisterAdmins() {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState("company_admin");
  const [forms, setForms] = useState({
    university_admin: { ...emptyForm },
    company_admin: { ...emptyForm },
    platform_admin: { ...emptyForm },
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false);

  const currentTab = TABS.find((t) => t.key === activeTab);
  const form = forms[activeTab];
  const companyOptions = organizations
    .filter((org) => org.org_type === "company")
    .map((org) => ({ value: org.id, label: org.name }));

  const selectedCompany = organizations.find(
    (org) => org.id === form.organization,
  );

  useEffect(() => {
    const loadOrganizations = async () => {
      setIsLoadingOrganizations(true);
      try {
        const data = await getOrganizations({ page: 1, pageSize: 100 });
        const items = Array.isArray(data?.organizations)
          ? data.organizations
          : [];
        setOrganizations(items);
      } catch (error) {
        showToast(error.message || "Failed to load organizations", "error");
      } finally {
        setIsLoadingOrganizations(false);
      }
    };

    loadOrganizations();
  }, [showToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForms((prev) => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], [name]: value },
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    const usernamePattern = /^[a-zA-Z][a-zA-Z0-9_]*$/;

    if (!form.username) {
      newErrors.username = "Username is required";
    } else if (!usernamePattern.test(form.username)) {
      newErrors.username =
        "Must start with a letter and contain only letters, numbers, and underscores";
    }

    if (!form.firstName) newErrors.firstName = "First name is required";
    if (!form.lastName) newErrors.lastName = "Last name is required";
    if (!form.email) newErrors.email = "Email is required";
    if (currentTab.showOrganization && !form.organization) {
      newErrors.organization = `${currentTab.organizationLabel} is required`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showToast("Please fix the errors in the form", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      if (activeTab === "company_admin") {
        await createAdminUser({
          role: activeTab,
          organizationName: selectedCompany?.name,
          organizationId: selectedCompany?.id,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          username: form.username,
        });
      }

      if (activeTab === "platform_admin") {
        await createPlatformAdminUser({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          username: form.username,
        });
      }

      showToast(
        "Admin created. They will receive an email to set their password.",
        "success",
      );

      setForms((prev) => ({ ...prev, [activeTab]: { ...emptyForm } }));
      setErrors({});
    } catch (error) {
      console.error("Failed to create admin:", error);

      if (error.payload?.detail && Array.isArray(error.payload.detail)) {
        const backendErrors = {};
        error.payload.detail.forEach((err) => {
          const field = err.loc?.[1];
          if (field) {
            const fieldMap = {
              first_name: "firstName",
              last_name: "lastName",
              organization_name: "organization",
            };
            backendErrors[fieldMap[field] || field] = err.msg;
          }
        });
        setErrors(backendErrors);
        showToast("Please fix the validation errors", "error");
      } else {
        showToast(error.message || "Failed to create admin user", "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="px-4 sm:px-8 py-8 space-y-6 min-h-screen">
        {/* Page header */}
        <div>
          <h1 className="text-2xl font-bold text-(--color-text-primary)">
            Register Admins
          </h1>
          <p className="text-xs mt-0.5 text-(--color-text-muted)">
            Register and manage platform administrators.
          </p>
        </div>

        <div className="rounded-lg bg-(--color-bg)/30 dark:bg-(--color-bg-card) border border-(--color-border)">
          {/* ── Tab bar ── icons only on mobile, icon + label on sm+ ── */}
          <div className="flex border-b border-(--color-border)">
            {TABS.map(({ key, label, icon: Icon, locked }) => {
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  type="button"
                  disabled={locked}
                  onClick={() => {
                    if (locked) return;
                    setActiveTab(key);
                    setErrors({});
                  }}
                  title={locked ? `${label} (coming soon)` : label}
                  className={`
        relative flex-1 flex items-center justify-center gap-2
        px-3 py-3.5 text-sm font-medium transition-colors duration-150
        ${
          locked
            ? "text-(--color-text-muted) opacity-50 cursor-not-allowed"
            : isActive
              ? "text-(--color-primary)"
              : "text-(--color-text-muted) hover:text-(--color-text-primary)"
        }
      `}
                >
                  <Icon size={16} className="shrink-0" />
                  <span className="hidden sm:inline whitespace-nowrap">
                    {label}
                  </span>
                  {locked && <Lock size={12} className="shrink-0" />}
                  {isActive && !locked && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-(--color-primary) rounded-full" />
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Form ── */}
          <form onSubmit={handleSubmit} className="px-4 sm:px-6 py-6 space-y-6">
            {/* Name + Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-(--color-text-primary)">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  placeholder="Enter first name"
                  className={`${inputClass} ${errors.firstName ? "border-red-500 focus:border-red-500" : ""}`}
                />
                {errors.firstName && (
                  <p className="text-xs text-red-500">{errors.firstName}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-(--color-text-primary)">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  placeholder="Enter last name"
                  className={`${inputClass} ${errors.lastName ? "border-red-500 focus:border-red-500" : ""}`}
                />
                {errors.lastName && (
                  <p className="text-xs text-red-500">{errors.lastName}</p>
                )}
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-sm font-medium text-(--color-text-primary)">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  className={`${inputClass} ${errors.email ? "border-red-500 focus:border-red-500" : ""}`}
                />
                {errors.email && (
                  <p className="text-xs text-red-500">{errors.email}</p>
                )}
              </div>
            </div>

            {/* Organization + Username */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {currentTab.showOrganization && (
                <div className="space-y-1.5">
                  {activeTab === "company_admin" ? (
                    <>
                      {/* ── Fixed: manual label so color matches other fields ── */}
                      <label className="text-sm font-medium text-(--color-text-primary)">
                        {currentTab.organizationLabel}
                      </label>
                      <div className="[&_button]:rounded-lg!">
                        <Select
                          name="organization"
                          value={form.organization}
                          onChange={handleChange}
                          options={companyOptions}
                          placeholder={
                            isLoadingOrganizations
                              ? "Loading organizations..."
                              : "Select company"
                          }
                          className="mb-0"
                          valueClassName="text-sm"
                          menuClassName="-mt-2"
                          triggerClassName={`min-h-0 h-[42px] rounded-lg px-4 py-2.5 border border-(--color-border) ${
                            errors.organization
                              ? "border-red-500 focus:border-red-500"
                              : ""
                          }`}
                          optionClassName="text-(--color-text-primary)"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <label className="text-sm font-medium text-(--color-text-primary)">
                        {currentTab.organizationLabel}
                      </label>
                      <input
                        type="text"
                        name="organization"
                        value={form.organization}
                        onChange={handleChange}
                        placeholder={currentTab.organizationPlaceholder}
                        className={`${inputClass} ${errors.organization ? "border-red-500 focus:border-red-500" : ""}`}
                      />
                    </>
                  )}
                  {errors.organization && (
                    <p className="text-xs text-red-500">
                      {errors.organization}
                    </p>
                  )}
                </div>
              )}

              <div
                className={`space-y-1.5 ${!currentTab.showOrganization ? "sm:col-span-2" : ""}`}
              >
                <label className="text-sm font-medium text-(--color-text-primary)">
                  Username
                </label>
                <input
                  type="text"
                  name="username"
                  value={form.username}
                  onChange={handleChange}
                  placeholder="Enter username"
                  className={`${inputClass} ${errors.username ? "border-red-500 focus:border-red-500" : ""}`}
                />
                {errors.username && (
                  <p className="text-xs text-red-500">{errors.username}</p>
                )}
              </div>
            </div>

            <p className="text-sm text-(--color-text-muted)">
              The new admin will receive an email to set their password. No
              password is required here.
            </p>

            {/* Submit */}
            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto px-6 py-2.5 rounded-lg text-sm font-medium bg-(--color-primary) text-(--color-text-on-primary) shadow-[0_0_10px_var(--color-shadow-primary)] hover:shadow-[0_0_16px_var(--color-shadow-primary-hover),0_0_32px_var(--color-shadow-primary)] transition-shadow duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Creating..." : "Create Account"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}
